/**
 * SickLeaveSummaryStats.js
 *
 * Summary statistics for sick leave, split into two sections:
 * current month and full year.
 *
 * SL-V2-3: Added month and selectedStaffId props. Replaced three-card layout
 * (total days, total hours, top staff) with a two-section layout showing
 * month and year totals. Staff member name used as section heading when a
 * specific staff member is selected. topStaff computation removed.
 *
 * SL-V3-2: Redesigned layout. Two panels side by side inside a single card,
 * separated by a thin vertical divider. Staff name heading demoted from h2
 * to a lightweight paragraph. Stacks vertically below 640 px.
 * No data logic changes.
 *
 * Props:
 *   year            {number}  - Selected year — drives full-year totals and heading fallback
 *   month           {number}  - Selected month 0–11 — drives month-section totals
 *   sickLeave       {Array}   - Year-scoped sick leave entries (already staff-scoped by subscription)
 *   staff           {Array}   - Staff list for name lookup
 *   selectedStaffId {string}  - Selected staff ID or 'all'
 *
 * Mirrors: VacationSummaryStats.js
 * Differences:
 *   - Two-section layout instead of three metric cards
 *   - No entitlement, carry-over, or remaining-days fields
 *   - No top-staff ranking
 *   - Staff member name replaces the year as heading when one is selected
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDurationFromMinutes } from '../../utils/TimeSlotUtils';

const SickLeaveSummaryStats = ({
  year,
  month,
  sickLeave = [],
  staff = [],
  selectedStaffId = 'all'
}) => {
  const { t } = useTranslation('sickLeave');

  /**
   * YYYY-MM prefix string for the selected month.
   * Used to filter the year-scoped subscription data client-side —
   * no second Firestore query needed.
   */
  const monthPrefix = useMemo(() => {
    return `${year}-${String(month + 1).padStart(2, '0')}`;
  }, [year, month]);

  /**
   * Sick leave entries belonging to the currently displayed calendar month.
   */
  const monthSickLeave = useMemo(() => {
    return sickLeave.filter(entry => {
      const dateStr = entry.getDateString ? entry.getDateString() : entry.date;
      return dateStr && dateStr.startsWith(monthPrefix);
    });
  }, [sickLeave, monthPrefix]);

  /**
   * Month totals — sick day count and sum of duration minutes.
   */
  const monthDays = useMemo(() => monthSickLeave.length, [monthSickLeave]);

  const monthMinutes = useMemo(() => {
    return monthSickLeave.reduce(
      (sum, entry) => sum + (entry.durationMinutes || 0),
      0
    );
  }, [monthSickLeave]);

  /**
   * Full year totals — sick day count and sum of duration minutes.
   * sickLeave is already scoped to the selected year by the subscription.
   */
  const yearDays = useMemo(() => sickLeave.length, [sickLeave]);

  const yearMinutes = useMemo(() => {
    return sickLeave.reduce(
      (sum, entry) => sum + (entry.durationMinutes || 0),
      0
    );
  }, [sickLeave]);

  /**
   * Resolve the staff member object for the heading.
   * Only attempted when a specific staff member is selected.
   */
  const staffMember = useMemo(() => {
    if (selectedStaffId === 'all') return null;
    return staff.find(s => s.id === selectedStaffId) ?? null;
  }, [selectedStaffId, staff]);

  /**
   * Section heading: staff member name when found, year string as fallback.
   * The component is only rendered when selectedStaffId !== 'all' (enforced
   * by the parent), so the fallback covers the brief window before staff data loads.
   */
  const sectionTitle = staffMember?.name ?? String(year);

  return (
    <div className="sick-leave-summary-stats">

      {/* Staff name or year — left-aligned, lightweight, not a heading element */}
      <p className="sl-stats-heading">{sectionTitle}</p>

      <div className="sl-stats-card">
        <div className="sl-stats-panels">

          {/* ── This Month ──────────────────────────────────────── */}
          <div className="sl-stats-panel">
            <span className="sl-stats-section-label">
              {t('thisMonth', { defaultValue: 'This Month' })}
            </span>

            <div className="sl-stats-metric">
              <span className="sl-stats-value">{monthDays}</span>
              <span className="sl-stats-unit">{t('sickDays')}</span>
            </div>

            <div className="sl-stats-metric">
              <span className="sl-stats-value">
                {formatDurationFromMinutes(monthMinutes)}
              </span>
              <span className="sl-stats-unit">{t('sickHours')}</span>
            </div>
          </div>

          {/* ── Vertical divider (becomes horizontal on mobile) ── */}
          <div className="sl-stats-divider" aria-hidden="true" />

          {/* ── Full Year ────────────────────────────────────────── */}
          <div className="sl-stats-panel">
            <span className="sl-stats-section-label">
              {t('fullYear', {
                year,
                defaultValue: `Full Year ${year}`
              })}
            </span>

            <div className="sl-stats-metric">
              <span className="sl-stats-value">{yearDays}</span>
              <span className="sl-stats-unit">{t('sickDays')}</span>
            </div>

            <div className="sl-stats-metric">
              <span className="sl-stats-value">
                {formatDurationFromMinutes(yearMinutes)}
              </span>
              <span className="sl-stats-unit">{t('sickHours')}</span>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};

export default SickLeaveSummaryStats;